<template>
  <div>
    <LoadingIndicator v-if="isLoading">Loading organizations...</LoadingIndicator>
    <template v-else>
      <div v-if="organizations?.length > 1" class="tabs">
        <button 
          v-for="organization in organizations" 
          :key="organization.id"
          :class="{ 'active': organization.id === selectedOrganization?.id }" 
          @click="selectOrganization(organization.id)"
        >
          {{ organization.name }}
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { getMyOrganizations, userKeys } from '@/scripts/api/userApi';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import { handleApiError } from '@/scripts/shared-utils';

const props = defineProps({
  // Optional initial organization ID to select
  initialOrganizationId: {
    type: String,
    default: null
  }
});

const emit = defineEmits([
  'organization-selected',
  'organizations-loaded'
]);

const queryClient = useQueryClient();

const selectedOrganization = ref(null);

// Query for organizations
const { data: organizations, isLoading } = useQuery({
  queryKey: userKeys.getMyOrganizations(),
  queryFn: getMyOrganizations,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching organizations')
});

// Watch for organizations to load and select the first one or the initial one
watch(organizations, (newOrganizations) => {
  if (newOrganizations && newOrganizations.length > 0) {
    // Emit that organizations have loaded
    emit('organizations-loaded', newOrganizations);
    
    // If initialOrganizationId is provided and exists in the list, select it
    if (props.initialOrganizationId) {
      const initialOrganization = newOrganizations.find(org => org.id === props.initialOrganizationId);
      if (initialOrganization) {
        selectedOrganization.value = initialOrganization;
        emit('organization-selected', initialOrganization);
        return;
      }
    }
    
    // If no initial org specified or not found, select the first one
    if (!selectedOrganization.value) {
      selectedOrganization.value = newOrganizations[0];
      emit('organization-selected', newOrganizations[0]);
    }
  }
}, { immediate: true });

// Function to select an organization
const selectOrganization = (id) => {
  const org = organizations.value.find(org => org.id === id) || null;
  selectedOrganization.value = org;
  emit('organization-selected', org);
};

// Function to refresh organization data
const refreshOrganizationData = () => {
  queryClient.invalidateQueries({ queryKey: userKeys.getMyOrganizations() });
};

// Expose properties and methods for the parent component
defineExpose({
  selectedOrganization,
  organizations,
  isLoadingOrganizations: isLoading,
  refreshOrganizationData
});
</script>

<style scoped>
@import '@/styles/shared-list.css';
@import '@/styles/shared-tabs.css';
</style>
